import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['rows', 'hiddenFields', 'count', 'unset']
  static values = { fieldsUrl: String, valuesUrl: String, group: Number, readonly: Boolean }

  connect() {
    // Seed this.pairs from server-rendered hidden-field wrappers (editable card).
    if (this.hasHiddenFieldsTarget) {
      this.pairs = [...this.hiddenFieldsTarget.children]
        .filter(wrapper => {
          const destroyInput = wrapper.querySelector('input[name$="[_destroy]"]')
          // Include wrappers with no _destroy input (built this session)
          // and wrappers whose _destroy is not 'true'
          return !destroyInput || destroyInput.value !== 'true'
        })
        .map(wrapper => wrapper.dataset.pair)
        .filter(Boolean)
    }
    // Read-only card: no hidden fields, seed from row elements.
    else if (this.hasRowsTarget) {
      this.pairs = [...this.rowsTarget.children]
        .map(row => row.dataset.pair)
        .filter(Boolean)
    }

    this.pairs = this.pairs || []
    this.syncCount()

    // LRPO-5767 follow-on — stamp server-assigned ids into our wrappers after
    // each autosave tick, exactly as territory_companies_controller and
    // location_picker_controller do. Without this, tick 2 re-posts a row that
    // already exists with no [id], Rails tries to create it again, uniqueness
    // rejects it, and the whole territory save rolls back every 30 seconds.
    this._onAutosaved = event => this.reconcileIds(event.detail)
    document.addEventListener('territory:autosaved', this._onAutosaved)

    // LRPO-5831 — mark every wrapper that is going out in THIS request. Once a
    // POST has carried a pair, the server may have created it, so retiring that
    // pair has to leave a wrapper behind to receive the id and destroy the row.
    // A pair that has never been posted cannot exist, so it is still removed
    // outright — which is what the value-change path relies on.
    this._onAutosaving = () => {
      if (!this.hasHiddenFieldsTarget) return
      Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
        wrapper.dataset.posted = 'true'
      })
    }
    document.addEventListener('territory:autosaving', this._onAutosaving)

    // LRPO-5784 — block the explicit Save when a demographic row has no value.
    this._modal = document.getElementById('update-confirmation-modal')
    if (this._modal) {
      this._onModalShow = event => this.validateBeforeSave(event)
      this._modal.addEventListener('show.bs.modal', this._onModalShow)
    }
  }

  disconnect() {
    document.removeEventListener('territory:autosaved', this._onAutosaved)
    document.removeEventListener('territory:autosaving', this._onAutosaving)
    if (this._modal) {
      this._modal.removeEventListener('show.bs.modal', this._onModalShow)
    }
  }

  /* ------ actions ------ */

  addRow() {
    if (this.readonlyValue) return

    const row = document.createElement('div')
    row.classList.add('territory-demo-row')

    const fieldSelect = this.createFieldSelect()
    row.appendChild(fieldSelect)

    const removeButton = this.createRemoveButton()
    row.appendChild(removeButton)

    this.rowsTarget.appendChild(row)

    // LRPO-5873 — QA: "Page should be scrolled too, when user add a lot new
    // demographic." The row was appended and nothing moved, so on a long form it
    // landed below the fold.
    //
    // ⚠ block: 'nearest', NOT 'center'. A row already on screen must not move the
    // page at all — otherwise every add jerks the view of someone who has
    // deliberately scrolled elsewhere. 'nearest' scrolls only when the row is
    // genuinely out of view.
    //
    // ⚠ KEEP THE typeof GUARD. jsdom does not implement scrollIntoView, and 19
    // jest files in this directory click this button. An unguarded call throws in
    // every one of them.
    //
    // Focus is the other half: the user clicked add, and the next thing they must
    // do is pick a type. preventScroll so focus does not fight the smooth scroll.
    if (fieldSelect && typeof fieldSelect.focus === 'function') {
      fieldSelect.focus({ preventScroll: true })
    }
    if (typeof row.scrollIntoView === 'function') {
      const reduce = typeof window !== 'undefined' && window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      row.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' })
    }

    // LRPO-5814 (QA return) — draw this row's operator NOW, provisionally. Without
    // this the slot only appeared once a type was picked, which is the "operator
    // appears with a short delay" QA reported: the strip visibly jumped.
    this.refreshOperators()
  }

  createRemoveButton() {
    const button = document.createElement('button')
    button.setAttribute('type', 'button')
    button.setAttribute('data-action', 'territory-demographics#removeRow')
    button.className = 'territory-demo-row__remove'
    button.setAttribute('aria-label', 'Remove this demographic')
    button.textContent = '×'
    return button
  }

  fieldChosen(event) {
    const select = event.target
    const row = select.closest('.territory-demo-row')

    // Remove any existing value select in the row
    const existingValueSelect = row.querySelector('select[data-role="value"]')
    if (existingValueSelect) {
      existingValueSelect.remove()
    }
    // LRPO-5761 — and any previous empty-state note, or switching from an
    // unanswered demographic to a populated one would leave the note sitting
    // beside a working dropdown.
    this.clearEmptyNote(row)

    // LRPO-5824 — AND THE TWO VALIDATION NOTES, which the type change has just
    // made untrue. The duplicate note was about the OLD pair; the missing-value
    // note was about the OLD type's value. Left behind, they sat on the row
    // contradicting it — QA's screenshot shows one row carrying both at once —
    // and appeared to "move between the type and value fields", because the value
    // select is rebuilt below while the stale note keeps its place in the row.
    this.clearDuplicateNote(row)
    this.clearMissingValueNote(row)

    // LRPO-5769 — the row no longer represents whatever pair it held: the TYPE
    // just changed. Retire it here rather than relying on the next valueChosen,
    // which is never reached when the new type has no values, when the user saves
    // without choosing one, or when the new pair is refused as a duplicate.
    const previousKey = row.dataset.pair
    if (previousKey) {
      this.retirePair(previousKey)
      delete row.dataset.pair

      const staleButton = row.querySelector('button[data-action="territory-demographics#removeRow"]')
      if (staleButton) staleButton.removeAttribute('data-pair')

      this.syncCount()
    }

    // LRPO-5814 — the operator depends on the row's TYPE, which has just changed,
    // and it must be right before a value is chosen. Recompute here as well as on
    // the value path: two rows showing only their types rendered no operator at
    // all, and before the field was read from this select they rendered "OR"
    // between two DIFFERENT types because both reported an empty dataset.pair.
    this.refreshOperators(row)

    const fieldId = select.value
    if (!fieldId) return

    // LRPO-5764 — SEQUENCE THE REQUESTS. The value list takes seconds on a
    // large event, so a user can easily change the demographic again before the
    // first response lands. Without a token the OLDER response still appends its
    // dropdown, leaving the row with two value selects — one listing values from
    // a demographic that is no longer chosen, so the pair the user then picks is
    // wrong. A slow request is exactly what makes this reachable.
    const token = (this._fetchToken || 0) + 1
    this._fetchToken = token
    row.dataset.fetchToken = String(token)

    this.appendLoader(row)

    const valuesUrl = `${this.valuesUrlValue}/${fieldId}/values`
    fetch(valuesUrl, { headers: { Accept: 'application/json' } })
      .then(response => response.json())
      .then(json => {
        // A newer choice has been made in this row — discard this response.
        if (row.dataset.fetchToken !== String(token)) return

        this.clearLoader(row)
        const values = json.values || []

        // LRPO-5761 — AN EMPTY LIST IS A NORMAL STATE, NOT AN ERROR. Value
        // options come from the EVENT's registrations, so a demographic nobody
        // has answered yet legitimately returns []. Rendering the select anyway
        // produced a tiny empty box containing only the blank placeholder —
        // nothing to choose and no reason given.
        //
        // A criterion is a PAIR (field + value), so a field with no available
        // values cannot become one. Emitting no control at all is therefore the
        // structurally correct outcome as well as the clearer one.
        if (values.length === 0) {
          this.appendEmptyNote(row)
          return
        }

        const valueSelect = this.createValueSelect(values)
        // LRPO-5766 — INSERT BEFORE THE REMOVE BUTTON, never append. The button
        // is added by valueChosen() once a pair is complete, so a later field
        // change appended the replacement dropdown AFTER it and stranded it
        // mid-row: [field] [value] [x] [value]. That is the exact layout in QA's
        // screenshot. The button must always be the row's last child.
        const removeBtn = row.querySelector('button[data-action="territory-demographics#removeRow"]')
        if (removeBtn) {
          row.insertBefore(valueSelect, removeBtn)
        } else {
          row.appendChild(valueSelect)
        }
      })
  }

  /* ------ insertion helper ------ */

  // LRPO-5766 — the remove button must stay the row's LAST child. addRow()
  // creates it up front (LRPO-5775), so anything added to a row afterwards has
  // to go BEFORE it, never appended.
  insertBeforeRemoveButton(row, element) {
    const removeBtn = row.querySelector('button[data-action="territory-demographics#removeRow"]')
    if (removeBtn) {
      row.insertBefore(element, removeBtn)
    } else {
      row.appendChild(element)
    }
  }

  /* ------ loading state ------ */

  // LRPO-5764 — shown while the value list is in flight. The request is slow
  // enough on a large event that silence reads as a broken page.
  appendLoader(row) {
    this.clearLoader(row)
    const loader = document.createElement('span')
    loader.className = 'territory-demo-row__loading'
    loader.dataset.role = 'loading'
    loader.setAttribute('role', 'status')
    loader.setAttribute('aria-live', 'polite')
    loader.textContent = 'Loading values…'
    this.insertBeforeRemoveButton(row, loader)
  }

  clearLoader(row) {
    const existing = row.querySelector('[data-role="loading"]')
    if (existing) existing.remove()
  }

  /* ------ empty state ------ */

  // LRPO-5761 — shown in place of the value dropdown when the event has no
  // recorded answers for the chosen demographic.
  appendEmptyNote(row) {
    const note = document.createElement('span')
    note.className = 'territory-demo-row__empty'
    note.dataset.role = 'empty-note'
    note.textContent = 'No values recorded for this demographic yet'
    this.insertBeforeRemoveButton(row, note)
  }

  clearEmptyNote(row) {
    const existing = row.querySelector('[data-role="empty-note"]')
    if (existing) existing.remove()
  }

  clearDuplicateNote(row) {
    const existing = row.querySelector('[data-role="duplicate-note"]')
    if (existing) existing.remove()
  }

  // LRPO-5805 — a duplicate note is only true while the pair it names is still
  // committed. Removing that pair from another row leaves the note asserting a
  // conflict that no longer exists (QA saw it with the counter at 0).
  //
  // ⚠ SWEEP BY KEY, NOT BLANKET. Two rows can be complaining about DIFFERENT
  // pairs; clearing them all would silence a note that is still correct. An
  // example pins that.
  clearStaleDuplicateNotes() {
    if (!this.hasRowsTarget) return

    this.rowsTarget.querySelectorAll('[data-role="duplicate-note"]').forEach(note => {
      const key = note.dataset.pair
      if (!key || !this.pairs.includes(key)) note.remove()
    })
  }

  /* ------ missing-value validation (LRPO-5784) ------ */

  validateBeforeSave(event) {
    if (this.readonlyValue) return

    // Clear any previous messages from all rows first, so a re-attempted save
    // does not stack duplicates and a row that has since been completed loses
    // its message.
    if (this.hasRowsTarget) {
      [...this.rowsTarget.children].forEach(row => this.clearMissingValueNote(row))
    }

    // Find incomplete rows: field chosen but value not chosen.
    // A row with no demographic chosen is NOT an error — the user clicked
    // "+ Add demographic" and has not started.
    const incompleteRows = []
    if (this.hasRowsTarget) {
      [...this.rowsTarget.children].forEach(row => {
        const fieldSelect = row.querySelector('select[data-role="field"]')
        const valueSelect = row.querySelector('select[data-role="value"]')
        if (fieldSelect && fieldSelect.value && (!valueSelect || !valueSelect.value)) {
          incompleteRows.push(row)
        }
      })
    }

    if (incompleteRows.length === 0) return

    event.preventDefault()
    incompleteRows.forEach(row => this.appendMissingValueNote(row))

    // LRPO-5813 — bring the FIRST offender into view. Save Changes sits in the
    // header and this card is the last thing on a long form, so blocking the save
    // silently looked to the user like the button did nothing.
    const first = incompleteRows[0]
    if (typeof first.scrollIntoView === 'function') {
      first.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  appendMissingValueNote(row) {
    const note = document.createElement('span')
    note.className = 'territory-demo-row__error'
    note.dataset.role = 'missing-value'
    note.textContent = 'Choose a value for this demographic'
    // LRPO-5766 — the remove button stays last.
    this.insertBeforeRemoveButton(row, note)
  }

  clearMissingValueNote(row) {
    const existing = row.querySelector('[data-role="missing-value"]')
    if (existing) existing.remove()
  }

  valueChosen(event) {
    const select = event.target
    const row = select.closest('.territory-demo-row')

    // LRPO-5784 — clear the validation message once a value has been chosen.
    this.clearMissingValueNote(row)

    // Clear any previous duplicate-note so choosing again clears the complaint.
    this.clearDuplicateNote(row)

    const fieldSelect = row.querySelector('select[data-role="field"]')

    const fieldId = fieldSelect.value
    const value = select.value

    // Do nothing if either is blank
    if (!fieldId || !value) return

    const key = `${fieldId}:${value}`

    // Revive a destroyed pair instead of creating a duplicate. Posting both a
    // destroy and a create for the same pair in one transaction races the unique
    // index on (territory_id, group_index, kind, custom_field_id, value) and can
    // raise RecordNotUnique, aborting the entire territory save inside the
    // 30-second autosave.
    if (this.hasHiddenFieldsTarget) {
      const destroyedWrapper = [...this.hiddenFieldsTarget.children]
        .find(w => w.dataset.pair === key &&
               (() => {
                 const di = w.querySelector('input[name$="[_destroy]"]')
                 return di && di.value === 'true'
               })())

      if (destroyedWrapper) {
        const destroyInput = destroyedWrapper.querySelector('input[name$="[_destroy]"]')
        if (destroyInput) {
          destroyInput.value = 'false'
          destroyInput.setAttribute('value', 'false')
        }

        // LRPO-5832 — remember that this wrapper came back from the dead. If a
        // save was already in flight carrying its _destroy=true, the server has
        // destroyed the record even though the flag now reads false, and the id it
        // still holds is stale. reconcileIds clears it when the response confirms
        // the id is gone. Scoped to revived wrappers ON PURPOSE: a blanket "id not
        // returned means strip it" would also fire when a card's criteria come back
        // under a different group_index and would re-create every row as a
        // duplicate.
        destroyedWrapper.dataset.revived = 'true'

        this.pairs.push(key)
        row.dataset.pair = key

        // LRPO-5769 — same rule as the main commit path below: the button must
        // point at the pair the row currently holds.
        let button = row.querySelector('button[data-action="territory-demographics#removeRow"]')
        if (!button) {
          button = document.createElement('button')
          button.setAttribute('type', 'button')
          button.setAttribute('data-action', 'territory-demographics#removeRow')
          button.textContent = '×'
          row.appendChild(button)
        }
        button.setAttribute('data-pair', key)

        this.syncCount()
        this.applyRowCount(row, select)
        return
      }
    }

    // Reject duplicate pairs
    if (this.pairs.includes(key)) {
      select.value = ''

      const note = document.createElement('span')
      note.className = 'territory-demo-row__duplicate'
      note.dataset.role = 'duplicate-note'
      note.dataset.pair = key
      note.textContent = `"${value}" is already added to this territory`
      this.insertBeforeRemoveButton(row, note)

      return
    }

    // LRPO-5769 — the row may already represent a DIFFERENT pair, because the
    // user changed the value rather than adding a new row. Retire the outgoing
    // one first, or it stays in the form and the territory keeps matching on a
    // value that is no longer on screen.
    const previousKey = row.dataset.pair
    if (previousKey && previousKey !== key) this.retirePair(previousKey)

    // Commit the pair
    this.pairs.push(key)
    row.dataset.pair = key

    // Ensure a remove button exists on the row, and that it points at the pair
    // the row CURRENTLY holds — a button left on the outgoing pair removes the
    // wrong entry, leaving this row on screen and the retired criterion live.
    let button = row.querySelector('button[data-action="territory-demographics#removeRow"]')
    if (!button) {
      button = document.createElement('button')
      button.setAttribute('type', 'button')
      button.setAttribute('data-action', 'territory-demographics#removeRow')
      button.textContent = '×'
      row.appendChild(button)
    }
    button.setAttribute('data-pair', key)

    // Append hidden fields
    this.appendHiddenFields(key, fieldId, value)

    // Update the count display
    this.syncCount()

    this.applyRowCount(row, select)
  }

  // LRPO-5881 — mirror the Companies card: the count is already known at
  // selection time (createValueSelect stamps it on the option), so show
  // "N with this answer" immediately instead of waiting for the next server
  // render. No data-criterion-id yet — the criterion has no id until it saves —
  // so live updates address it only after the next re-render, same as a fresh
  // company chip.
  applyRowCount(row, select) {
    const option = select.selectedOptions && select.selectedOptions[0]
    const raw = option && option.dataset ? option.dataset.leadCount : undefined
    if (raw === undefined) return
    const count = Number(raw)
    if (!Number.isFinite(count)) return

    let countEl = row.querySelector('.territory-demo-row__count')
    if (!countEl) {
      countEl = document.createElement('span')
      countEl.className = 'territory-demo-row__count'
      countEl.setAttribute('data-autosave-ignore', '')
      countEl.dataset.countSuffix = 'with this answer'
      this.insertBeforeRemoveButton(row, countEl)
    }
    countEl.textContent = count === 0 ? '0 with this answer' : `${count} with this answer`
  }

  // LRPO-5769 — retire a pair this row no longer represents.
  //
  // Extracted from removeRow so the two callers cannot drift: removing a row and
  // CHANGING a row's value both have to retire the outgoing pair, and they have
  // to do it the same way. The persisted-vs-session distinction is the load-bearing
  // half — a criterion already in the database keeps its wrapper (which carries the
  // [id]) and is marked _destroy, because deleting the wrapper would leave the
  // criterion live in the database while the page says it is gone.
  retirePair(key) {
    if (!key) return

    this.pairs = this.pairs.filter(p => p !== key)

    if (!this.hasHiddenFieldsTarget) return

    const wrapper = [...this.hiddenFieldsTarget.children]
      .find(w => w.dataset.pair === key)
    if (!wrapper) return

    // LRPO-5831 — keep the wrapper only when the row may exist on the server
    // (already persisted with a criterionId, or posted since it was built).
    // Otherwise remove it outright so value-change flicks leave no trace.
    if (wrapper.dataset.criterionId || wrapper.dataset.posted === 'true') {
      // LRPO-5660 — a session-built wrapper has no [_destroy] input; create one.
      let destroyInput = wrapper.querySelector('input[name$="[_destroy]"]')

      if (!destroyInput) {
        const sample = wrapper.querySelector('input[name]')
        if (!sample) return
        destroyInput = document.createElement('input')
        destroyInput.type = 'hidden'
        destroyInput.setAttribute('name', sample.getAttribute('name').replace(/\[\w+\]$/, '[_destroy]'))
        wrapper.appendChild(destroyInput)
      }

      destroyInput.value = 'true'
      destroyInput.setAttribute('value', 'true')
    } else {
      wrapper.remove()
    }
  }

  // LRPO-5775 — A row is deletable before it becomes a pair (no data-pair yet).
  // The button's own row is removed via closest('.territory-demo-row') so that
  // clicking delete on one empty row does not take its siblings with it.
  removeRow(event) {
    // Stimulus 3 always supplies event.params as an object, so reading the pair
    // key from it would give undefined. Read the dataset from the button instead.
    const button = event.currentTarget || event.target
    const key = button.dataset.pair

    if (key) {
      // LRPO-5769 — pairs bookkeeping and wrapper retirement now live in
      // retirePair, so removal and value-change cannot drift apart.
      this.retirePair(key)
    }

    // Remove the clicked button's row directly
    const row = button.closest('.territory-demo-row')
    if (row) {
      row.remove()
    }

    // Remove any other rows sharing the same pair key
    if (key && this.hasRowsTarget) {
      [...this.rowsTarget.children]
        .filter(row => row.dataset.pair === key)
        .forEach(row => row.remove())
    }

    this.clearStaleDuplicateNotes()

    this.syncCount()
  }

  /* ------ hidden fields ------ */

  appendHiddenFields(key, fieldId, value) {
    if (!this.hasHiddenFieldsTarget) return

    const index = this.nextIndex()
    // ⚠️ LRPO-5767 — NO `d` PREFIX. Strong parameters only accepts an INTEGER
    // nested-attributes key (/\A-?\d+\z/); a `d`-prefixed row is filtered away
    // before accepts_nested_attributes_for ever sees it, so the demographic
    // silently never saved. `nextIndex()` below scans EVERY
    // territory_criteria_attributes input on the page, including the Companies
    // card's, so a plain integer here cannot collide with one of theirs.
    const namePrefix = `territory[territory_criteria_attributes][${index}]`

    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-pair', key)

    const fields = [
      { name: 'kind', value: 'demographic' },
      { name: 'custom_field_id', value: fieldId },
      { name: 'value', value: value },
      { name: 'group_index', value: String(this.groupValue || 0) }
    ]

    fields.forEach(({ name, value }) => {
      const input = document.createElement('input')
      input.setAttribute('type', 'hidden')
      input.setAttribute('name', `${namePrefix}[${name}]`)
      input.setAttribute('value', value)
      wrapper.appendChild(input)
    })

    this.hiddenFieldsTarget.appendChild(wrapper)
  }

  /* ------ index allocation ------ */

  nextIndex() {
    const inputs = document.querySelectorAll(
      'input[name^="territory[territory_criteria_attributes]"]'
    )

    let maxIndex = -1
    inputs.forEach(input => {
      const match = input.name.match(/\[territory_criteria_attributes\]\[([^\]]+)\]/)
      if (match) {
        // LRPO-5767 — `d`-prefixed keys are no longer emitted, but legacy markup
        // already rendered into a page mid-session may still carry one, so keep
        // tolerating the prefix when READING. Never write one (see appendHiddenFields).
        const digits = match[1].replace(/^d/, '')
        if (/^\d+$/.test(digits)) {
          const num = parseInt(digits, 10)
          if (num > maxIndex) {
            maxIndex = num
          }
        }
      }
    })

    return maxIndex + 1
  }

  /* ------ helpers ------ */

  createFieldSelect() {
    const select = document.createElement('select')
    select.setAttribute('data-role', 'field')
    select.setAttribute('data-action', 'change->territory-demographics#fieldChosen')
    select.className = 'territory-demo-row__select territory-demo-row__select--field'
    select.setAttribute('aria-label', 'Demographic type')

    // LRPO-5766 — same rule as the value dropdown: a disabled prompt rather
    // than a blank selectable option.
    const placeholder = document.createElement('option')
    placeholder.value = ''
    placeholder.textContent = 'Select a demographic'
    placeholder.disabled = true
    placeholder.selected = true
    select.appendChild(placeholder)

    fetch(this.fieldsUrlValue, { headers: { Accept: 'application/json' } })
      .then(response => response.json())
      .then(json => {
        json.fields.forEach(field => {
          const option = document.createElement('option')
          option.setAttribute('value', String(field.id))
          option.textContent = field.label
          select.appendChild(option)
        })
      })

    return select
  }

  createValueSelect(values) {
    const select = document.createElement('select')
    select.setAttribute('data-role', 'value')
    select.setAttribute('data-action', 'change->territory-demographics#valueChosen')
    select.className = 'territory-demo-row__select territory-demo-row__select--value'
    select.setAttribute('aria-label', 'Demographic value')

    // LRPO-5766 — a DISABLED prompt, not a blank selectable row. An empty option
    // that can be chosen lets the user pick "nothing" back after choosing a
    // value, which is not a criterion and silently leaves the row half-built.
    // `selected` keeps it showing until a real value is picked.
    const placeholder = document.createElement('option')
    placeholder.value = ''
    placeholder.textContent = 'Select a value'
    placeholder.disabled = true
    placeholder.selected = true
    select.appendChild(placeholder)

    values.forEach(v => {
      const option = document.createElement('option')
      option.setAttribute('value', v.value)
      option.dataset.leadCount = String(v.lead_count)
      option.textContent = `${v.value} (${v.lead_count})`
      select.appendChild(option)
    })

    return select
  }

  /* ------ id reconciliation ------ */

  // LRPO-5767 follow-on — see connect(). Matches on the PAIR
  // (custom_field_id + value), because that is what identifies a demographic;
  // the companies card matches on name, which is not enough here — the same
  // value under a different field is a different criterion.
  reconcileIds(detail) {
    if (!detail || !Array.isArray(detail.territory_criteria)) return
    if (!this.hasHiddenFieldsTarget) return

    const ourGroup = Number(this.groupValue || 0)
    const ours = detail.territory_criteria.filter(c =>
      c.kind === 'demographic' && Number(c.group_index) === ourGroup
    )

    // Never hand out an id already claimed by a wrapper anywhere on the page.
    const claimed = new Set(
      Array.from(document.querySelectorAll('input[name$="[id]"]')).map(el => el.value)
    )

    Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
      if (wrapper.querySelector('input[name$="[id]"]')) return   // already has one

      const pair = wrapper.dataset.pair
      if (!pair) return

      const match = ours.find(server =>
        `${server.custom_field_id}:${server.value}` === pair &&
        !claimed.has(String(server.id))
      )
      if (!match) return

      claimed.add(String(match.id))
      wrapper.dataset.criterionId = String(match.id)

      // LRPO-5881 — same rule as the companies card (LRPO-5875): the id is what
      // the live count writer queries by ([data-criterion-id][data-count-suffix]).
      // Stamp the COUNT SPAN, never the row — the writer sets textContent on
      // whatever it matches, and an id on the row would erase the whole row.
      if (this.hasRowsTarget) {
        const row = [...this.rowsTarget.children].find(r => r.dataset.pair === pair)
        const rowCount = row && row.querySelector('.territory-demo-row__count')
        if (rowCount) rowCount.dataset.criterionId = String(match.id)
      }

      // Derive the [id] input's name from a sibling so it shares this row's
      // nested-attributes index — an id under a different index would describe
      // a different record.
      const sample = wrapper.querySelector('input[name]')
      if (!sample) return

      const idName = sample.getAttribute('name').replace(/\[\w+\]$/, '[id]')
      const input = document.createElement('input')
      input.type = 'hidden'
      input.setAttribute('name', idName)
      input.setAttribute('value', String(match.id))
      wrapper.prepend(input)
    })

    // LRPO-5788 — remove wrappers whose destroy has COMPLETED.
    //
    // retirePair keeps a destroyed wrapper so Rails sees the _destroy and
    // performs the deletion. Once the save succeeds the record is gone, but the
    // wrapper stayed in the DOM carrying its [id] — so the next autosave posted
    // an id that no longer exists and RecordNotFound aborted the whole territory
    // save. Every tick after that failed the same way until reload.
    //
    // The server's response lists the criteria that still exist, so a destroyed
    // wrapper whose id is NOT in that list has been dealt with and can go.
    const returnedIds = new Set(ours.map(c => String(c.id)))
    Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
      if (!wrapper.dataset.criterionId) return

      const stillLive = returnedIds.has(wrapper.dataset.criterionId)
      const destroyInput = wrapper.querySelector('input[name$="[_destroy]"]')

      if (destroyInput && destroyInput.value === 'true' && !stillLive) {
        wrapper.remove()
        return
      }

      // LRPO-5832 — a REVIVED wrapper whose id the server no longer returns. The
      // save in flight when the user re-added carried _destroy=true, so the record
      // really was destroyed even though the flag now reads false. Posting that id
      // again dies on RecordNotFound and takes the whole territory save with it.
      // Strip it and let the row post as the create it now is.
      //
      // ⚠ Keyed on data-revived, NOT on "the id was not returned" — `ours` is
      // filtered by group_index, so the broader rule would strip every id on a card
      // whose criteria came back under a different index.
      if (wrapper.dataset.revived === 'true' && !stillLive) {
        const idInput = wrapper.querySelector('input[name$="[id]"]')
        if (idInput) idInput.remove()
        delete wrapper.dataset.criterionId
      }

      if (stillLive) delete wrapper.dataset.revived
    })

    // LRPO-5831 — clear the temporary stamp: reconciliation is done, so the
    // wrappers that survived now carry a criterionId (or will get one next time).
    Array.from(this.hiddenFieldsTarget.children)
      .forEach(wrapper => delete wrapper.dataset.posted)

    this.refreshOperators()
  }

  // LRPO-5800 — the counter and "not set" badge disagreed: the badge was server-rendered inside
  // `<% if criteria.empty? %>` with no Stimulus target, so nothing on the client could hide it.
  // Add a criterion and the header reads "Demographics 1" directly above a badge insisting
  // nothing is set. Now the badge tracks the count via the `unset` target so the two always agree.
  // The badge is NOT hidden merely because the count is zero — its text explains matching
  // behaviour that a bare "0" does not convey. A bare 0 reads like "nothing matches", which is
  // the opposite of the truth.
  syncCount() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = String(this.pairs.length)
    }
    if (this.hasUnsetTarget) this.unsetTarget.hidden = this.pairs.length > 0
    this.refreshOperators()
  }

  // LRPO-5814 — the field a row belongs to.
  //
  // ⚠ READ THE SELECT, NOT dataset.pair. `dataset.pair` is only set once a VALUE
  // is chosen, so two half-configured rows both reported "" , compared EQUAL, and
  // rendered "OR" between two DIFFERENT types (QA's screenshot at 17:44). The
  // field select exists as soon as the type is picked. dataset.pair stays as the
  // fallback for server-rendered rows.
  fieldOf(row) {
    const select = row.querySelector('select[data-role="field"]')
    if (select && select.value) return String(select.value)
    return String(row.dataset.pair || '').split(':')[0]
  }

  // LRPO-5814 (QA return) — put rows of the same field together.
  //
  // ★★ THE ENGINE GROUPS BY FIELD, SO THE SCREEN MUST TOO. Territory#group_matches?
  // does group_by(&:custom_field_id): values of one field are OR'd, different
  // fields are AND'd, and ROW ORDER PLAYS NO PART. Operators computed from row
  // adjacency therefore described a rule that does not exist — a Business Type row
  // sitting under a Volume row read "AND" while the engine was OR-ing it with the
  // other Business Type rows.
  //
  // ⚠ Field groups keep FIRST-APPEARANCE order and rows keep their order within a
  // group, so the list stays as close to what the user typed as grouping allows.
  // ⚠ This moves DOM rows only. Every submitted input lives in the separate
  // hiddenFields container, so reordering cannot change one posted parameter.
  reorderRowsByField() {
    if (!this.hasRowsTarget) return

    const rows = [...this.rowsTarget.querySelectorAll('.territory-demo-row')]
    if (rows.length < 2) return

    const groups = new Map()
    rows.forEach(row => {
      const field = this.fieldOf(row)
      if (!groups.has(field)) groups.set(field, [])
      groups.get(field).push(row)
    })

    const desired = []
    groups.forEach(group => group.forEach(row => desired.push(row)))

    // ⚠ DO NOTHING IF NOTHING MOVES. appendChild on a row that is already in place
    // still detaches and re-inserts it, and MEASURED that is 6 childList mutations
    // per call on already-grouped rows. Those rows sit inside #exhibitor-form,
    // whose MutationObserver schedules a full-form POST on every mutation — so an
    // unconditional re-append turns "recompute the operators" into "save the whole
    // territory", whose completion recomputes them again. That is LRPO-5826.
    if (desired.every((row, index) => row === rows[index])) return

    desired.forEach(row => this.rowsTarget.appendChild(row))
  }

  // LRPO-5814 — make the matching rule visible between rows: the same field
  // twice is an OR, two different fields are an AND. Recomputed from DOM order
  // rather than appended, so server-rendered rows and rows the user just added
  // are identical by construction, and deleting the first row correctly
  // promotes the second.
  refreshOperators(movedRow = null) {
    if (!this.hasRowsTarget) return

    this.reorderRowsByField()

    const rows = [...this.rowsTarget.querySelectorAll('.territory-demo-row')]
    let previousField = null

    rows.forEach((row, index) => {
      const existing = row.querySelector('[data-role="operator"]')

      delete row.dataset.justMoved

      if (movedRow !== null && row === movedRow) {
        row.dataset.justMoved = 'true'
      }

      const field = this.fieldOf(row)

      if (index === 0) {
        // First row must never carry an operator — remove only if present.
        if (existing) existing.remove()
        previousField = field
        return
      }

      // Compute what the operator should be for this row.
      let expectedText, expectedPending
      if (field === '' || previousField === '') {
        expectedText = 'AND'
        expectedPending = true
      } else {
        expectedText = field === previousField ? 'OR' : 'AND'
        expectedPending = false
      }

      // LRPO-5826 — idempotence: leave the operator alone when it is already
      // correct, so reconcileIds doesn't churn the DOM and trigger another
      // autosave. Check both text content AND pending flag for equality.
      //
      // ⚠ Compare pending as a BOOLEAN. A row that is not pending carries no
      // data-pending attribute at all, so dataset.pending is undefined, and
      // String(undefined) is "undefined" — which never equals String(false). That
      // made every non-pending operator fail the check and get rebuilt on each
      // save, which is precisely the churn this fix exists to remove.
      const existingPending = existing ? existing.dataset.pending === 'true' : false

      if (existing &&
          existing.textContent.trim().toUpperCase() === expectedText.toUpperCase() &&
          existingPending === expectedPending) {
        previousField = field
        return
      }

      if (existing) existing.remove()

      const op = document.createElement('span')
      op.className = 'territory-demo-row__operator'
      op.dataset.role = 'operator'

      if (expectedPending) {
        op.dataset.pending = 'true'
        op.textContent = expectedText
      } else {
        op.textContent = expectedText
      }

      row.prepend(op)

      previousField = field
    })
  }
}
